import React, { FC, Suspense, useMemo, useContext } from "react";
import { ErrorBoundary } from "react-error-boundary";

import {
	RecurringFrequencyMap,
	RecurringShowBeforeMap,
} from "./AddNewTaskDialog";
import TaskAttachmentDropzone from "./TaskAttachmentDropzone";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TaskSave } from "@/features/task/model/Task/TaskRequest";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomTextAreaArray from "@/shared/forms/CustomTextareaArray";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertToast } from "@/shared/notifications/AlertToast";
import { CloseActionButton, SaveActionButton } from "@/shared/ui";
import ComponentError from "@/shared/ui/errors/Components";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Form, Formik, FormikHelpers } from "formik";
import * as yup from "yup";

import { useUpdateTask } from "@/service/TaskQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezone);

/* ─────────────────── Types ─────────────────── */
interface Option {
	Id: number;
	Name: string;
}

interface TaskUserDto {
	Id?: number;
}

interface TaskAttachmentDto {
	FileName?: string;
}

interface TaskRecurringConfigurationDto {
	ShowBefore?: string;
	Frequency?: string;
	ReccuringTemplateId?: number;
}

interface DetailedTaskDto {
	Title?: string;
	Description?: string;
	Deadline?: string | Date;
	ReccuringConfiguration?: TaskRecurringConfigurationDto | null;
	SelectedSystem?: number;
	Handover?: boolean;
	Shiftlog?: boolean;
	Tags?: string[];
	GroupResponsibleList?: number[];
	TaskGroupId?: number;
	UserResponsibleList?: number[];
	User?: TaskUserDto | null;
	Attachment?: TaskAttachmentDto | null;
	applyToActiveChildren?: boolean;
}

interface EditTaskDialogProps {
	open: boolean;
	handleClose: () => void;
	onSaved?: (taskId: number) => void;
	title: string;
	taskId: number;
	detailedTask: DetailedTaskDto | null;
	groupOptions: Option[];
	userOptions: Option[];
	systemOptions: Option[];
	enforceFutureDeadline?: boolean;
}

interface FormValues {
	title: string;
	description: string;
	deadline: string;
	showBefore: string;
	frequency: string;
	system: number;
	handover: boolean;
	shiftlog: boolean;
	tags: string[];
	selectedGroups: Option[];
	selectedUsers: Option[];
	fileName: string;
	file: File | null;
	removeFile: boolean;
	reccuringTemplateId: number;
	applyToActiveChildren: boolean;
}

const toErrorText = (value: unknown): string | undefined =>
	typeof value === "string" ? value : undefined;

const toIdList = (value: unknown): number[] => {
	if (typeof value === "string") {
		return value
			.split(",")
			.map((v) => Number(v))
			.filter((v) => Number.isFinite(v));
	}
	if (Array.isArray(value)) {
		return value.map((v) => Number(v)).filter((v) => Number.isFinite(v));
	}
	return [];
};

/* ─────────────────── UI tokens (pastel & rounded) ─────────────────── */
const COL = {
	chipBg: "#EEF2FF",
	chipText: "#3730A3",
};

const roundedInput = {
	"& .MuiOutlinedInput-root": { borderRadius: 2 },
	"& .MuiInputBase-input": { fontWeight: 500 },
};

/* ─────────────────── Validation ─────────────────── */
const dateTimeInputFormat = "YYYY-MM-DDTHH:mm";

const buildValidationSchema = (
	enforceFutureDeadline: boolean,
	timezone: string,
) =>
	yup
		.object()
		.shape({
			title: yup.string().required("Title is required"),
			description: yup.string().required("Description is required"),
			deadline: yup
				.string()
				.required("Deadline is required")
				.test(
					"deadline-not-in-past",
					"Deadline must be now or later",
					(value) =>
						!enforceFutureDeadline ||
						!value ||
						!dayjs
							.tz(value, dateTimeInputFormat, timezone)
							.isBefore(dayjs().tz(timezone).startOf("minute")),
				),
			selectedGroups: yup
				.array()
				.of(
					yup.object().shape({
						Id: yup.number().required(),
						Name: yup.string().required(),
					}),
				)
				.required(),
			selectedUsers: yup
				.array()
				.of(
					yup.object().shape({
						Id: yup.number().required(),
						Name: yup.string().required(),
					}),
				)
				.notRequired(),
		})
		.test(
			"at-least-one-responsible",
			"At least one responsible group or user must be selected",
			function (value) {
				const { selectedGroups, selectedUsers } = (value || {}) as FormValues;
				const hasGroup =
					Array.isArray(selectedGroups) && selectedGroups.length > 0;
				const hasUser =
					Array.isArray(selectedUsers) && selectedUsers.length > 0;
				if (!hasGroup && !hasUser) {
					return this.createError({
						path: "selectedGroups",
						message: "At least one responsible group or user must be selected",
					});
				}
				return true;
			},
		);

/* ─────────────────── Component ─────────────────── */
const EditTaskDialog: FC<EditTaskDialogProps> = ({
	open,
	handleClose,
	onSaved,
	title,
	taskId,
	detailedTask,
	groupOptions,
	userOptions,
	systemOptions,
	enforceFutureDeadline = false,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { updateTask, isSubmitting } = useUpdateTask();
	const { timezone } = useTime();
	const validationSchema = useMemo(
		() => buildValidationSchema(enforceFutureDeadline, timezone),
		[enforceFutureDeadline, timezone],
	);
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 1.5, md: 2 },
	};
	const minimumDeadline = enforceFutureDeadline
		? dayjs().tz(timezone).format(dateTimeInputFormat)
		: undefined;

	// Skeleton state when loading details
	if (!detailedTask && open) {
		return (
			<ErrorBoundary
				fallbackRender={({ error, resetErrorBoundary }) => (
					<ComponentError
						message={
							error instanceof Error ? error.message : "Unexpected error"
						}
						reset={resetErrorBoundary}
					/>
				)}
			>
				<Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
					<Dialog
						open={open}
						fullScreen={isMobile}
						maxWidth="md"
						fullWidth
						PaperProps={{ sx: { ...sharedTaskDialogPaperSx(theme, isMobile) } }}
					>
						<DialogTitle
							sx={{ ...sharedTaskDialogHeaderSx(theme), px: 2.5, py: 1.5 }}
						>
							<Translate String="EditTaskDialog.Loading">
								<Typography variant="h6">Loading...</Typography>
							</Translate>
						</DialogTitle>
						<DialogContent
							sx={{
								...sharedTaskDialogContentSx(theme),
								display: "flex",
								justifyContent: "center",
								py: 4,
							}}
						>
							<CircularProgress />
						</DialogContent>
					</Dialog>
				</Suspense>
			</ErrorBoundary>
		);
	}

	// Derive initial selections
	const groupIdsFromTask: number[] = Array.from(
		new Set<number>([
			...(Array.isArray(detailedTask?.GroupResponsibleList)
				? detailedTask.GroupResponsibleList
				: []),
			...(typeof detailedTask?.TaskGroupId === "number"
				? [detailedTask.TaskGroupId]
				: []),
		]),
	);

	const userIdsFromTask = Array.from(
		new Set([
			...(Array.isArray(detailedTask?.UserResponsibleList)
				? detailedTask.UserResponsibleList
				: []),
			...(detailedTask?.User?.Id ? [detailedTask.User.Id] : []),
		]),
	);

	const selectedGroupOptions: Option[] = groupIdsFromTask
		.map((id) => groupOptions.find((g) => g.Id === id))
		.filter((g): g is Option => !!g);

	const selectedUserOptions: Option[] = userIdsFromTask
		.map((id: number) => userOptions.find((u) => u.Id === id))
		.filter((u): u is Option => Boolean(u));

	// Convert local datetime to UTC ISO
	const localInputToUtcIso = (localValue: string, tz: string) =>
		dayjs.tz(localValue, tz).utc().format();

	const initialValues: FormValues = {
		title: detailedTask?.Title || "",
		description: detailedTask?.Description || "",
		deadline: dayjs
			.utc(detailedTask?.Deadline)
			.tz(timezone)
			.format("YYYY-MM-DDTHH:mm"),
		showBefore: detailedTask?.ReccuringConfiguration?.ShowBefore || "OnDue",
		frequency: detailedTask?.ReccuringConfiguration?.Frequency || "Never",
		system: detailedTask?.SelectedSystem || systemOptions[0]?.Id || 0,
		handover: Boolean(detailedTask?.Handover),
		shiftlog: Boolean(detailedTask?.Shiftlog),
		reccuringTemplateId:
			detailedTask?.ReccuringConfiguration?.ReccuringTemplateId,
		tags: detailedTask?.Tags || [],
		selectedGroups: selectedGroupOptions,
		selectedUsers: selectedUserOptions,
		fileName: detailedTask?.Attachment?.FileName || "",
		file: null,
		removeFile: false,
		applyToActiveChildren: Boolean(detailedTask?.applyToActiveChildren),
	};

	const onSubmit = async (
		values: FormValues,
		{ setSubmitting }: FormikHelpers<FormValues>,
	) => {
		setSubmitting(true);

		const groupsResponsibleIds = Array.from(
			new Set(values.selectedGroups.map((g) => g.Id)),
		);
		const usersResponsibleIds = Array.from(
			new Set(values.selectedUsers.map((u) => u.Id)),
		);
		const primaryUserId = usersResponsibleIds[0] ?? 0;

		try {
			const payload: TaskSave = {
				TaskId: taskId,
				title: values.title,
				description: values.description,
				deadline: localInputToUtcIso(values.deadline, timezone),
				showBefore: values.showBefore,
				ReccuringFrequency: values.frequency,
				// keep legacy "system" for backwards compatibility,
				// but also send correct API field "SelectedSystem"
				system: values.system,
				SelectedSystem: values.system,
				handover: values.handover ? 1 : 0,
				shiftlog: values.shiftlog ? 1 : 0,
				tags: values.tags,
				TaskGroupId: groupsResponsibleIds[0] ?? 0,
				GroupsResponsible: groupsResponsibleIds,
				UsersResponsible: usersResponsibleIds,
				UserId: primaryUserId,
				reccuringTemplateId: values.reccuringTemplateId,
				applyToActiveChildren: values.applyToActiveChildren === true,
				removeFile: values.removeFile,
			};

			if (values.file) {
				payload.File = values.file;
				payload.FileName = values.file.name;
			} else if (
				values.fileName &&
				values.fileName !== detailedTask?.Attachment?.FileName
			) {
				payload.FileName = values.fileName;
			}

			await updateTask({ taskId, request: payload });
			await AlertToast.success("Task updated");
			onSaved?.(taskId);
			handleClose();
		} catch (error) {
			console.error(error);
			await AlertToast.failed("Failed to update task");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="lg"
			fullScreen={isMobile}
			aria-labelledby="edit-task-dialog-title"
			PaperProps={{
				sx: {
					...sharedTaskDialogPaperSx(theme, isMobile),
				},
			}}
		>
			{/* Header */}
			<DialogTitle
				id="edit-task-dialog-title"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					pr: 7,
					pl: { xs: 2, md: 3 },
					py: 1.5,
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: 900 }}>
					<Translate String="EditTaskDialog.EditTask">Edit Task</Translate>{" "}
					<span style={{ opacity: 0.6 }}>·</span> {title} #{taskId}
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.EditTaskDialog.L442C17.Close", "Close")}
					onClick={handleClose}
					disabled={isSubmitting}
					size="small"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			{/* Body */}
			<DialogContent
				dividers
				sx={{
					...sharedTaskDialogContentSx(theme),
					p: { xs: 2, md: 3 },
					overflowY: "auto",
					WebkitOverflowScrolling: "touch",
					flex: 1,
					minHeight: 0,
				}}
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={onSubmit}
					enableReinitialize
				>
					{({
						values,
						handleChange,
						setFieldValue,
						touched,
						errors,
						isSubmitting,
					}) => (
						<Form>
							<Grid container spacing={2.25}>
								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
										<Grid container spacing={2.25}>
											<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										id="title"
										name="title"
										label={
											<Translate String="EditTaskDialog.Title">Title</Translate>
										}
										value={values.title}
										onChange={handleChange}
										disabled={isSubmitting}
										error={Boolean(touched.title && errors.title)}
										helperText={
											touched.title ? toErrorText(errors.title) : undefined
										}
										aria-required="true"
										aria-describedby="title-error-text"
										sx={roundedInput}
									/>
											</Grid>
											<Grid item xs={12} md={6}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(touched.system && errors.system)}
										sx={roundedInput}
									>
										<InputLabel id="system-label">
											<Translate String="EditTaskDialog.System">
												System
											</Translate>
										</InputLabel>
										<Select
											labelId="system-label"
											id="system"
											name="system"
											value={values.system}
											onChange={(e: SelectChangeEvent<number | string>) =>
												handleChange(e)
											}
											label={
												<Translate String="EditTaskDialog.System">
													System
												</Translate>
											}
											aria-required="true"
											aria-describedby="system-error-text"
											MenuProps={{
												PaperProps: { elevation: 3, sx: { borderRadius: 2 } },
											}}
										>
											{systemOptions.map((sys) => (
												<MenuItem key={sys.Id} value={sys.Id}>
													{sys.Name.trim()}
												</MenuItem>
											))}
										</Select>
										{touched.system && errors.system && (
											<FormHelperText id="system-error-text">
												{toErrorText(errors.system)}
											</FormHelperText>
										)}
									</FormControl>
											</Grid>
										</Grid>
									</Paper>
								</Grid>

								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
										<Grid container spacing={2.25}>
											<Grid item xs={12} md={8}>
										<SATOSEditorField
											id="description"
											name="description"
											label="Description"
											disabled={isSubmitting}
											aria-required="true"
											aria-describedby="description-error-text"
											style={{ minWidth: "100%" }}
											imageUploadDisabled={true}
										/>
										{touched.description && errors.description && (
											<FormHelperText error sx={{ mt: 0.5 }}>
												{toErrorText(errors.description)}
											</FormHelperText>
										)}
											</Grid>
											<Grid item xs={12} md={4}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(touched.tags && errors.tags)}
									>
										<CustomTextAreaArray
											name="tags"
											label="Tags"
											disabled={isSubmitting}
										/>
										{touched.tags && errors.tags && (
											<FormHelperText>
												{toErrorText(errors.tags)}
											</FormHelperText>
										)}
									</FormControl>
									<Box mt={1}>
										<CustomCheckbox
											name="shiftlog"
											label="Shiftlog"
											disabled={isSubmitting}
										/>
									</Box>
									<Box mt={1}>
										<CustomCheckbox
											name="handover"
											label="Handover"
											disabled={isSubmitting}
										/>
									</Box>
											</Grid>
										</Grid>
									</Paper>
								</Grid>

								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
										<Grid container spacing={2.25}>
											<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										id="deadline"
										name="deadline"
										label={
											<Translate String="EditTaskDialog.Deadline">
												Deadline
											</Translate>
										}
										type="datetime-local"
										value={values.deadline}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
										inputProps={
											minimumDeadline ? { min: minimumDeadline } : undefined
										}
										disabled={isSubmitting}
										error={Boolean(touched.deadline && errors.deadline)}
										helperText={
											touched.deadline
												? toErrorText(errors.deadline)
												: undefined
										}
										aria-required="true"
										aria-describedby="deadline-error-text"
										sx={roundedInput}
									/>
											</Grid>
											<Grid item xs={12} md={4}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(touched.frequency && errors.frequency)}
										sx={roundedInput}
									>
										<InputLabel id="frequency-label">
											<Translate String="Task.EditTaskDialog.Repeat">
												Repeat
											</Translate>
										</InputLabel>
										<Select
											labelId="frequency-label"
											id="frequency"
											name="frequency"
											value={values.frequency}
											onChange={(e: SelectChangeEvent<string>) =>
												handleChange(e)
											}
											label={
												<Translate String="EditTaskDialog.Repeat">
													Repeat
												</Translate>
											}
											aria-describedby="frequency-error-text"
											MenuProps={{
												PaperProps: { elevation: 3, sx: { borderRadius: 2 } },
											}}
										>
											{Object.entries(RecurringFrequencyMap).map(
												([label, val]) => (
													<MenuItem key={val} value={val}>
														{label}
													</MenuItem>
												),
											)}
										</Select>
										{touched.frequency && errors.frequency && (
											<FormHelperText id="frequency-error-text">
												{toErrorText(errors.frequency)}
											</FormHelperText>
										)}
									</FormControl>
											</Grid>
											<Grid item xs={12} md={4}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(touched.showBefore && errors.showBefore)}
										sx={roundedInput}
									>
										<InputLabel id="showBefore-label">
											<Translate String="EditTaskDialog.ShowBefore">
												Show Before
											</Translate>
										</InputLabel>
										<Select
											labelId="showBefore-label"
											id="showBefore"
											name="showBefore"
											value={values.showBefore}
											onChange={(e: SelectChangeEvent<string>) =>
												handleChange(e)
											}
											label={
												<Translate String="EditTaskDialog.ShowBefore">
													Show Before
												</Translate>
											}
											aria-describedby="showBefore-error-text"
											MenuProps={{
												PaperProps: { elevation: 3, sx: { borderRadius: 2 } },
											}}
										>
											{Object.entries(RecurringShowBeforeMap).map(
												([label, val]) => (
													<MenuItem key={val} value={val}>
														{label}
													</MenuItem>
												),
											)}
										</Select>
										{touched.showBefore && errors.showBefore && (
											<FormHelperText id="showBefore-error-text">
												{toErrorText(errors.showBefore)}
											</FormHelperText>
										)}
									</FormControl>
											</Grid>
										</Grid>
									</Paper>
								</Grid>

								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
										<Translate String="EditTaskDialog.Attachment">
											Attachment
										</Translate>
									</Typography>
									<TaskAttachmentDropzone
										fileName={values.fileName}
										disabled={isSubmitting}
										onFileSelected={(file) => {
											void setFieldValue("file", file);
											void setFieldValue("fileName", file.name);
											void setFieldValue("removeFile", false);
										}}
										onClear={() => {
											void setFieldValue("file", null);
											void setFieldValue("fileName", "");
											void setFieldValue("removeFile", true);
										}}
									/>
										{touched.file &&
											toErrorText((errors as { file?: unknown }).file) && (
												<Typography
													id="file-error-text"
													variant="caption"
													color="error"
													sx={{ mt: 0.5, display: "block" }}
												>
													{toErrorText((errors as { file?: unknown }).file)}
												</Typography>
											)}
									</Paper>
								</Grid>

								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
										<Grid container spacing={2.25}>
											<Grid item xs={12} md={6}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(
											touched.selectedGroups && errors.selectedGroups,
										)}
										sx={roundedInput}
									>
										<InputLabel id="groups-label">
											<Translate String="EditTaskDialog.ResponsibleGroups">
												Responsible Groups
											</Translate>
										</InputLabel>
										<Select
											labelId="groups-label"
											id="selectedGroups"
											name="selectedGroups"
											multiple
											value={values.selectedGroups.map((g) => g.Id)}
											onChange={(e: SelectChangeEvent<number[]>) => {
												const ids = toIdList(e.target.value);
												const selectedOpts = ids
													.map((id) => groupOptions.find((g) => g.Id === id))
													.filter((g): g is Option => Boolean(g));
												void setFieldValue("selectedGroups", selectedOpts);
											}}
											label={
												<Translate String="EditTaskDialog.ResponsibleGroups">
													Responsible Groups
												</Translate>
											}
											aria-required="true"
											aria-describedby="groups-error-text"
											renderValue={(selected) => (
												<Box
													sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
												>
													{toIdList(selected).map((id) => {
														const item = groupOptions.find((g) => g.Id === id);
														return item ? (
															<Chip
																key={id}
																label={item.Name}
																size="small"
																sx={{
																	bgcolor: COL.chipBg,
																	color: COL.chipText,
																	fontWeight: 700,
																}}
															/>
														) : null;
													})}
												</Box>
											)}
											MenuProps={{
												PaperProps: { elevation: 3, sx: { borderRadius: 2 } },
											}}
										>
											{groupOptions.map((opt) => (
												<MenuItem key={opt.Id} value={opt.Id}>
													{opt.Name}
												</MenuItem>
											))}
										</Select>
										{touched.selectedGroups && errors.selectedGroups && (
											<FormHelperText
												id="groups-error-text"
												sx={{ color: "error.main" }}
											>
												{toErrorText(errors.selectedGroups)}
											</FormHelperText>
										)}
									</FormControl>
											</Grid>
											<Grid item xs={12} md={6}>
									<FormControl
										fullWidth
										disabled={isSubmitting}
										error={Boolean(
											touched.selectedUsers && errors.selectedUsers,
										)}
										sx={roundedInput}
									>
										<InputLabel id="users-label">
											<Translate String="EditTaskDialog.ResponsibleUsers">
												Responsible Users
											</Translate>
										</InputLabel>
										<Select
											labelId="users-label"
											id="selectedUsers"
											name="selectedUsers"
											multiple
											value={values.selectedUsers.map((u) => u.Id)}
											onChange={(e: SelectChangeEvent<number[]>) => {
												const ids = toIdList(e.target.value);
												const selectedOpts = ids
													.map((id) => userOptions.find((u) => u.Id === id))
													.filter((u): u is Option => !!u);
												void setFieldValue("selectedUsers", selectedOpts);
											}}
											label={
												<Translate String="EditTaskDialog.ResponsibleUsers">
													Responsible Users
												</Translate>
											}
											aria-required="false"
											aria-describedby="users-error-text"
											renderValue={(selected) => (
												<Box
													sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
												>
													{toIdList(selected).map((id) => {
														const item = userOptions.find((u) => u.Id === id);
														return item ? (
															<Chip
																key={id}
																label={item.Name}
																size="small"
																sx={{ bgcolor: "#F1F5F9", fontWeight: 700 }}
															/>
														) : null;
													})}
												</Box>
											)}
											MenuProps={{
												PaperProps: { elevation: 3, sx: { borderRadius: 2 } },
											}}
										>
											{userOptions.map((opt) => (
												<MenuItem key={opt.Id} value={opt.Id}>
													{opt.Name}
												</MenuItem>
											))}
										</Select>
										{touched.selectedUsers && errors.selectedUsers && (
											<FormHelperText id="users-error-text">
												{toErrorText(errors.selectedUsers)}
											</FormHelperText>
										)}
									</FormControl>
											</Grid>
										</Grid>
									</Paper>
								</Grid>

								<Grid item xs={12}>
									<Paper variant="outlined" sx={sectionCardSx}>
										<Grid container spacing={2.25}>
											<Grid item xs={12} md={6}>
									<FormControl fullWidth disabled={isSubmitting}>
										<FormControlLabel
											control={
												<Checkbox
													checked={values.reccuringTemplateId === taskId}
													onChange={(e) => {
														void setFieldValue(
															"reccuringTemplateId",
															e.target.checked ? taskId : 0,
														);
													}}
													color="primary"
												/>
											}
											label={
												<Translate String="EditTaskDialog.UseRecurringTemplate">
													Use Recurring Template
												</Translate>
											}
										/>
									</FormControl>
									<FormControl fullWidth disabled={isSubmitting}>
										<FormControlLabel
											control={
												<Checkbox
													checked={values.applyToActiveChildren}
													onChange={(e) => {
														void setFieldValue(
															"applyToActiveChildren",
															e.target.checked,
														);
													}}
													color="primary"
												/>
											}
											label={
												<Translate String="EditTaskDialog.ApplyToActiveChildren">
													Apply to active children
												</Translate>
											}
										/>
									</FormControl>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
							</Grid>

							{/* Footer */}
							<DialogActions
								sx={{
									...sharedTaskDialogActionsSx(theme, false),
									mt: 3,
									px: 0,
									pt: 2,
								}}
							>
								<Box sx={{ flex: 1 }} />
								<CloseActionButton
									onClick={handleClose}
									startIcon={<CloseIcon />}
									disabled={isSubmitting}
									sx={{
										borderRadius: 2,
										textTransform: "none",
										fontWeight: 800,
									}}
								>
									<Translate String="EditTaskDialog.Cancel">Cancel</Translate>
								</CloseActionButton>
								<SaveActionButton
									type="submit"
									startIcon={
										isSubmitting ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											<SaveIcon />
										)
									}
									disabled={isSubmitting}
									sx={{
										borderRadius: 2,
										textTransform: "none",
										fontWeight: 900,
									}}
								>
									{isSubmitting ? (
										<Translate String="EditTaskDialog.Saving">
											Saving…
										</Translate>
									) : (
										<Translate String="EditTaskDialog.Update">Update</Translate>
									)}
								</SaveActionButton>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default EditTaskDialog;
